import React from 'react';
import { ProposalState } from '../../models/clinicAgent';
import { proposalTitle, isApplicable } from '../../services/clinicAgentProposals';

interface ProposalCardProps {
  proposal: ProposalState;
  onConfirm: () => void;
  onDiscard: () => void;
}

// Render a payload as readable key/value lines (shallow; nested values stringified).
function renderPayload(payload: Record<string, unknown>): React.ReactNode {
  const entries = Object.entries(payload).filter(([k]) => k !== 'diary_item_id');
  if (entries.length === 0) return null;
  return (
    <div style={{ marginTop: 6, display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '2px 8px' }}>
      {entries.map(([key, value]) => (
        <React.Fragment key={key}>
          <span style={{ color: '#6b7280', fontSize: 12 }}>{key}</span>
          <span style={{ color: '#1f2937', fontSize: 12, wordBreak: 'break-word' }}>
            {value == null
              ? '—'
              : typeof value === 'object'
                ? JSON.stringify(value)
                : String(value)}
          </span>
        </React.Fragment>
      ))}
    </div>
  );
}

const ProposalCard: React.FC<ProposalCardProps> = ({ proposal, onConfirm, onDiscard }) => {
  const applicable = isApplicable(proposal.action);
  const { status } = proposal;

  return (
    <div
      style={{
        border: '1px solid #e5e7eb',
        borderLeft: '3px solid #38b2ac',
        borderRadius: 10,
        background: '#fff',
        padding: 12,
        marginTop: 8,
      }}
    >
      <div style={{ fontWeight: 600, fontSize: 13, color: '#1f2937' }}>
        {proposalTitle(proposal.action)}
      </div>
      {proposal.rationale && (
        <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4, fontStyle: 'italic' }}>
          {proposal.rationale}
        </div>
      )}
      {renderPayload(proposal.payload || {})}

      {!applicable && (
        <div style={{ fontSize: 12, color: '#b45309', marginTop: 8 }}>
          Acción no reconocida — solo lectura.
        </div>
      )}

      {status === 'error' && proposal.error && (
        <div style={{ fontSize: 12, color: '#dc2626', marginTop: 8 }}>{proposal.error}</div>
      )}

      {applicable && status !== 'applied' && status !== 'discarded' && (
        <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
          <button
            onClick={onConfirm}
            disabled={status === 'applying'}
            style={{
              border: 'none',
              borderRadius: 8,
              padding: '6px 14px',
              background: status === 'applying' ? '#9fd8d4' : '#38b2ac',
              color: '#fff',
              fontWeight: 600,
              fontSize: 13,
              cursor: status === 'applying' ? 'default' : 'pointer',
            }}
          >
            {status === 'applying' ? 'Aplicando…' : status === 'error' ? 'Reintentar' : 'Confirmar'}
          </button>
          <button
            onClick={onDiscard}
            disabled={status === 'applying'}
            style={{
              border: '1px solid #e5e7eb',
              borderRadius: 8,
              padding: '6px 14px',
              background: '#fff',
              color: '#6b7280',
              fontWeight: 600,
              fontSize: 13,
              cursor: status === 'applying' ? 'default' : 'pointer',
            }}
          >
            Descartar
          </button>
        </div>
      )}

      {status === 'applied' && (
        <div style={{ fontSize: 12, color: '#059669', marginTop: 10, fontWeight: 600 }}>
          ✓ Aplicado
        </div>
      )}

      {status === 'discarded' && (
        <div style={{ fontSize: 12, color: '#6b7280', marginTop: 10, fontWeight: 600 }}>
          Descartado
        </div>
      )}
    </div>
  );
};

export default ProposalCard;
